import { Controller } from "@hotwired/stimulus"
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart,
  LinearScale,
  Tooltip,
  Legend
} from "chart.js"
import { weatherTooltipPlugin } from "./chart_weather_tooltip"

Chart.register(BarController, BarElement, CategoryScale, LinearScale, Tooltip, Legend)

// 目標線を描画するカスタムプラグイン
const goalLinePlugin = {
  id: "goalLine",
  afterDraw(chart) {
    const goalValue = chart.options.plugins.goalLine?.value
    if (goalValue === null || goalValue === undefined) return

    const { ctx } = chart
    const yScale = chart.scales.y
    const xScale = chart.scales.x
    const y = yScale.getPixelForValue(goalValue)

    ctx.save()
    ctx.beginPath()
    ctx.setLineDash([6, 4])
    ctx.strokeStyle = "#C74E4E"
    ctx.lineWidth = 2
    ctx.moveTo(xScale.left, y)
    ctx.lineTo(xScale.right, y)
    ctx.stroke()
    ctx.restore()
  }
}

Chart.register(goalLinePlugin)

const PX_PER_DAY_2WEEKS = 60

export default class extends Controller {
  static targets = ["canvas"]
  static values = {
    labels: Array,
    values: Array,
    colors: Array,
    goal: String,
    unit: String,
    weather: Array,
    period: String
  }

  connect() {
    const goalValue = this.goalValue ? parseFloat(this.goalValue) : null
    const numericValues = this.valuesValue.filter((value) => Number.isFinite(value))
    const dataMax = numericValues.length ? Math.max(...numericValues) : 0
    const baseMax = Math.max(dataMax, goalValue || 0)
    const yAxisMax = baseMax > 0 ? baseMax * 1.1 : 1
    const weather = this.weatherValue

    if (this.periodValue === "2weeks") {
      this.setupScrollableChart()
    }

    this.chart = new Chart(this.canvasTarget, {
      type: "bar",
      data: {
        labels: this.labelsValue,
        datasets: [
          {
            data: this.valuesValue,
            backgroundColor: this.colorsValue,
            borderRadius: 2,
            maxBarThickness: 20
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        plugins: {
          legend: { display: false },
          tooltip: weatherTooltipPlugin(weather, this.labelsValue, (dp) => {
            const val = dp.parsed.y
            if (val === null || val === undefined) return ""
            const rounded = Number.isInteger(val) ? val : parseFloat(val.toFixed(1))
            return `${rounded} ${this.unitValue}`
          }),
          goalLine: {
            value: goalValue
          }
        },
        scales: {
          x: {
            grid: { display: false },
            title: {
              display: true,
              text: "(日)",
              font: { size: 10 }
            },
            ticks: {
              font: { size: 10 },
              maxRotation: 0
            }
          },
          y: {
            beginAtZero: true,
            suggestedMax: yAxisMax,
            grid: { color: "#ECECEC" },
            title: {
              display: true,
              text: `(${this.unitValue})`,
              font: { size: 10 }
            },
            ticks: {
              font: { size: 10 }
            }
          }
        }
      }
    })

    if (this.periodValue === "2weeks") {
      this.scrollToEnd()
    }
  }

  setupScrollableChart() {
    const container = this.element
    const existing = container.querySelector("[data-scrollable-wrapper]")
    if (existing) {
      existing.style.width = `${this.labelsValue.length * PX_PER_DAY_2WEEKS}px`
      return
    }
    container.style.overflowX = "auto"
    const wrapper = document.createElement("div")
    wrapper.style.height = "100%"
    wrapper.style.width = `${this.labelsValue.length * PX_PER_DAY_2WEEKS}px`
    wrapper.setAttribute("data-scrollable-wrapper", "")
    container.appendChild(wrapper)
    wrapper.appendChild(this.canvasTarget)
  }

  scrollToEnd() {
    this.element.scrollLeft = this.element.scrollWidth
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy()
      this.chart = null
    }
    const wrapper = this.element.querySelector("[data-scrollable-wrapper]")
    if (wrapper) {
      wrapper.remove()
    }
  }
}
